import { PostApiAuthUpdateTokensApiResponse } from "@entities/auth";
import {
  BaseQueryApi,
  BaseQueryFn,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
  retry,
} from "@reduxjs/toolkit/query";
import { LOCAL_STORAGE } from "@shared/constants";
import {
  API_PREFIX,
  API_URL,
  ApiResponse,
  CheckSentryEnable,
  CheckSentryExceptionsStatusCodes,
} from "@shared/index";
import { useSentryCaptureException } from "../hooks/useSentryCaptureException";

type BaseQueryWithReauth = BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  object,
  FetchBaseQueryMeta
>;

async function refreshAccessToken(): Promise<void> {
  const refreshToken = localStorage.getItem(LOCAL_STORAGE.RefreshToken);
  const sessionId = localStorage.getItem(LOCAL_STORAGE.SessionId);
  const telegramId = localStorage.getItem(LOCAL_STORAGE.TelegramId);
  const userId = localStorage.getItem(LOCAL_STORAGE.UserId);

  const response = await fetch(`${API_URL}${API_PREFIX}/auth/update-tokens`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      refresh_token: refreshToken,
      session_id: sessionId,
      telegram_id: Number(telegramId),
      user_id: userId,
    }),
  });

  if (!response.ok)
    throw new Error(`Failed to refresh token: ${response.statusText}`);

  const {
    data,
    status,
    message,
  }: ApiResponse<PostApiAuthUpdateTokensApiResponse> = await response.json();

  if (status === "error")
    throw new Error(`Failed to refresh token: ${message}`);

  localStorage.setItem(LOCAL_STORAGE.AccessToken, data.access_token as string);
  localStorage.setItem(LOCAL_STORAGE.RefreshToken, data.refresh_token);
  localStorage.setItem(LOCAL_STORAGE.SessionId, data.session_id);
}

export const createBaseQueryWithReauth = (
  uri: string,
  onUnauthorized: () => void
): BaseQueryWithReauth => {
  return async (
    args: string | FetchArgs,
    api: BaseQueryApi,
    extraOptions: object
  ) => {
    const baseQuery = retry(
      fetchBaseQuery({
        baseUrl: uri,
        prepareHeaders: (headers) => {
          const accessToken = localStorage.getItem(LOCAL_STORAGE.AccessToken);
          if (accessToken)
            headers.set("Authorization", `Bearer ${accessToken}`);

          return headers;
        },
      }),
      { maxRetries: 0 }
    );

    let result = await baseQuery(args, api, extraOptions);

    if (CheckSentryEnable() && CheckSentryExceptionsStatusCodes(result))
      useSentryCaptureException(result.error);

    if (
      result.error &&
      result.error.data === "Unauthorized" &&
      !uri.includes("/auth/authorization")
    ) {
      try {
        await refreshAccessToken();
        result = await baseQuery(args, api, extraOptions);
      } catch (error) {
        console.error("Token refresh failed:", error);
        onUnauthorized();
      }
    }

    return result;
  };
};
